// "AI spend today: $X of $Y" — the workspace's daily AI spend cap at a glance.
// Used in the Agents page header and the floating agent bubble.
import { Link } from "react-router-dom";
import { Gauge } from "lucide-react";
import { cn } from "@/lib/utils";
import type { AiSpendStatus } from "./shared";

const usd = (n: number) => (n >= 100 ? `$${n.toFixed(0)}` : `$${n.toFixed(2)}`);

const resetLabel = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "midnight" : d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

export function SpendMeter({ spend, canEditCap, className }: { spend: AiSpendStatus | null | undefined; canEditCap?: boolean; className?: string }) {
  if (!spend) return null;
  const { spentUsd, capUsd, reached } = spend;
  const pct = capUsd ? Math.min(100, Math.round((spentUsd / capUsd) * 100)) : 0;
  const near = !!capUsd && !reached && pct >= 80;
  return (
    <div className={cn("rounded-md border px-3 py-2 text-xs", reached ? "border-destructive/50 bg-destructive/10" : "border-border", className)}
      role="status" aria-label="AI spend today">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 font-medium">
          <Gauge className={cn("size-3.5", reached ? "text-destructive" : near ? "text-amber-500" : "text-muted-foreground")} />
          AI spend today: {usd(spentUsd)}{capUsd != null ? ` of ${usd(capUsd)}` : ""}
        </span>
        {canEditCap && <Link to="/settings?tab=ai-spend" className="text-[11px] text-muted-foreground underline-offset-2 hover:underline">{capUsd == null ? "Set a cap" : "Change cap"}</Link>}
      </div>
      {capUsd != null && (
        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
          <div className={cn("h-full rounded-full", reached ? "bg-destructive" : near ? "bg-amber-500" : "bg-primary")} style={{ width: `${pct}%` }} />
        </div>
      )}
      {reached ? (
        <p className="mt-1.5 text-destructive">
          Daily cap reached. AI work is paused until {resetLabel(spend.resetsAt)}{canEditCap ? ", or raise the cap in Settings → AI spend" : ". Ask an owner or admin to raise it"}.
        </p>
      ) : capUsd == null ? (
        <p className="mt-1 text-muted-foreground">No daily cap is set for this workspace.</p>
      ) : null}
    </div>
  );
}
